import { Button } from "@sellar-io/components";
import {
  OrderFulfillmentType,
  OrderGetResponseType,
  OrderPaymentType,
  SupplierInvoiceCreationStageType,
} from "@sellar-io/api-v2/types";
import dayjs from "dayjs";
import { ReactElement } from "react";
import { useForm, useWatch } from "react-hook-form";
import { SpinnerLegacy } from "../../../components";
import { Alert } from "../../../components/alert/Alert";
import {
  DialogNavigation,
  DialogContent,
  DialogCTA,
  DialogPage,
} from "../../../components/modal/Dialog";
import { useFullPageDialogContext } from "../../../components/modal/useFullPageDialogContext";
import { useEvents } from "../../../hooks";
import { apiV2AssertError } from "../../../hooks/api/v2/apiV2Request";
import { useOrderConfirm } from "../../../hooks/api/v2/order/v2/useOrderConfirm";
import { useSupplierRelationshipList } from "../../../hooks/api/v2/supplierRelationship/useSupplierRelationshipList";
import { useRemoteFeatureFlags } from "../../../hooks/useRemoteFeatureFlags";
import { useSellarCurrentBusiness } from "../../../hooks/useSellarCurrentBusiness.shared";
import { FullDutyAlert } from "../Alerts/FullDutyAlert";
import { ConfirmOrderDialogPage } from "./ConfirmOrderDialog.shared";
import { ConfirmOrderModalFormFragment } from "./ConfirmOrderModalFormFragment";
import { ConfirmOrderSellarShippingFulfillmentPage } from "./ConfirmOrderSellarShippingFulfillmentPage";
import { BadPayerWarning } from "../../../components/badPayerWarning/BadPayerWarning";
import { OrderV2Dto } from "@sellar-io/api-v2/types";
import { validateFulfillmentTypeChange } from "./SellarDistributionFulfillmentChangeFormFragment/lib/validateFulfillmentTypeChange";
import { getIsSwitchingBuyerOrderFromSellarDistribution } from "./SellarDistributionFulfillmentChangeFormFragment/lib/isSwitchingBuyerOrderFromSD";

export type OrderConfirmationFormType = {
  fulfillmentType: OrderFulfillmentType;
  paymentDue: string | null;
  hasNoDraughtDutyRelief: boolean;
  deliveryPlannedAt: string | null;
  dispatchPlannedAt: string | null;
  paymentType: OrderPaymentType | undefined;
  purchaseOrderNumber: string | null;
  paidAt: Date | null;
  dispatchedAt?: Date | null;
  deliveredAt?: Date | null;
  customInvoiceNumber: string | null;
  deliveryNumber: string | null;
  deliveryNotes: string | null;
  fulfillmentChangeReason: OrderV2Dto.OrderConfirmRequestType["fulfillmentChangeReason"];
  fulfillmentChangeReasonMessage: NonNullable<
    OrderV2Dto.OrderConfirmRequestType["fulfillmentChangeReasonMessage"]
  >;
  fulfillmentChangeAcknowledged: boolean;
};

const isSellarShipping = (
  order: OrderGetResponseType,
  fulfillmentType: OrderFulfillmentType | "sellar-shipping",
) => {
  return (
    ((order.fulfillmentType === "dxBasic" ||
      order.fulfillmentType === "ups" ||
      order.fulfillmentType === "dxDropOff" ||
      order.fulfillmentType === "courier" ||
      order.fulfillmentType === "apc") &&
      fulfillmentType === order.fulfillmentType) ||
    fulfillmentType === ("sellar-shipping" as OrderFulfillmentType)
  );
};
export function ConfirmOrderForm({
  onDismiss,
  order,
  invoiceCreationStage,
  onSuccess,
}: {
  onDismiss: () => void;
  onSuccess?: () => void;
  order: OrderGetResponseType;
  invoiceCreationStage: SupplierInvoiceCreationStageType;
}): ReactElement | null {
  const { setPage, page } = useFullPageDialogContext();
  const orderConfirmMutation = useOrderConfirm(order.id);
  const { track } = useEvents();
  const { business } = useSellarCurrentBusiness();
  const remoteFeatureFlagQuery = useRemoteFeatureFlags();
  const supplierRelationshipQuery = useSupplierRelationshipList({
    query: [
      {
        type: "buyerId",
        modifier: "EQUALS",
        value: order.RetailerId,
      },
    ],
  });
  const paymentTerms =
    supplierRelationshipQuery.data?.pages[0]?.[0]?.paymentTerms;
  const form = useForm<OrderConfirmationFormType>({
    shouldUseNativeValidation: true,
    defaultValues: {
      fulfillmentType: ["dxBasic", "ups", "courier", "dxDropOff"].includes(
        order.fulfillmentType,
      )
        ? ("sellar-shipping" as OrderFulfillmentType)
        : order.fulfillmentType,
      paymentDue: order.paymentDue
        ? dayjs(order.paymentDue).format("YYYY-MM-DD")
        : dayjs().add(1, "day").format("YYYY-MM-DD"),
      hasNoDraughtDutyRelief: order.hasNoDraughtDutyRelief,
      deliveryPlannedAt: order.deliveryPlannedAt
        ? dayjs(order.deliveryPlannedAt).format("YYYY-MM-DD")
        : null,
      dispatchPlannedAt: order.dispatchPlannedAt
        ? dayjs(order.dispatchPlannedAt).format("YYYY-MM-DD")
        : null,
      paymentType: order.paymentType ?? undefined,
      purchaseOrderNumber: order.purchaseOrderNumber,
      paidAt: order.paidAt,
      customInvoiceNumber: order.customInvoiceNumber ?? null,
      deliveryNumber: order.deliveryNumber ?? null,
      deliveryNotes: order.deliveryNotes ?? null,
      fulfillmentChangeReason: undefined,
      fulfillmentChangeReasonMessage: "",
      fulfillmentChangeAcknowledged: false,
    } satisfies OrderConfirmationFormType,
  });

  const fulfillmentType = useWatch({
    control: form.control,
    name: "fulfillmentType",
  });
  const isFulfillmentValid =
    fulfillmentType !== undefined &&
    [
      "sellarDistribution",
      "direct",
      "courier",
      "collection",
      "haulier",
      "dx",
      "ups",
      "apc",
      "dxBasic",
      "dxDropOff",
      "parcelforce",
    ].includes(fulfillmentType);

  const onSubmit = async (data: OrderConfirmationFormType) => {
    const validation = validateFulfillmentTypeChange({
      data,
      isSwitchingBuyerOrderFromSellarDistribution:
        getIsSwitchingBuyerOrderFromSellarDistribution({
          order,
          fulfillmentType: data.fulfillmentType,
        }),
    });

    if (!validation.success) {
      validation.errors.forEach(([field, error]) => {
        form.setError(field, error);
      });
      return;
    }

    // oxlint-disable-next-line react/purity -- Captured when this deferred submit handler runs.
    const confirmedAt = new Date();
    if (
      page === ConfirmOrderDialogPage.FORM &&
      isSellarShipping(order, fulfillmentType)
    ) {
      return setPage(ConfirmOrderDialogPage.FULFILLMENT);
    }

    if (data.fulfillmentType === "dxBasic") {
      throw new Error(
        "Cannot confirm an order with fulfillment type dx or dxBasic",
      );
    }

    const canOrderPricingDetailsChange =
      !order.paidAt && !order.invoiceIssuedAt;

    // Legacy stripe order migration: orders created before payment links existed
    // may still have stripe as their payment type. If the feature flag is active and
    // the order hasn't been paid/invoiced/had a stripe invoice created, convert to
    // manual and request a payment link so the buyer can still pay online.
    let paymentType = data.paymentType;
    let paymentLinkRequested = false;
    if (
      paymentType === "stripe" &&
      remoteFeatureFlagQuery.isActive("checkoutPaymentLinkRequest") &&
      canOrderPricingDetailsChange &&
      !order.stripeInvoiceId
    ) {
      paymentType = "manual";
      paymentLinkRequested = true;
    }

    const deliveryPlannedAt = data.deliveryPlannedAt
      ? dayjs(data.deliveryPlannedAt).toDate()
      : undefined;

    let dispatchPlannedAt = data.dispatchPlannedAt
      ? dayjs(data.dispatchPlannedAt).toDate()
      : undefined;

    if (["direct", "collection"].includes(fulfillmentType)) {
      dispatchPlannedAt = deliveryPlannedAt;
    }

    const setPaymentDue =
      (invoiceCreationStage === "confirmed" && !order.paymentDue) ||
      paymentTerms === "paymentOnDelivery";

    await orderConfirmMutation.mutateAsync(
      {
        confirmedAt,
        fulfillmentType: data.fulfillmentType,
        // NOTE: estimated delivery dates not set for order for payment type card payment with deposit
        ...(order.paymentType !== "cardPaymentWithDeposit" && {
          deliveryPlannedAt,
          dispatchPlannedAt,
        }),
        ...(data.purchaseOrderNumber && {
          purchaseOrderNumber: data.purchaseOrderNumber,
        }),
        paymentLinkRequested,
        ...(canOrderPricingDetailsChange && {
          ...(data.paidAt && { paidAt: data.paidAt }),
          ...(paymentType && { paymentType }),
          ...("hasNoDraughtDutyRelief" in data && {
            hasNoDraughtDutyRelief: data.hasNoDraughtDutyRelief,
          }),
          ...(setPaymentDue &&
            data.paymentDue && {
              paymentDue: dayjs(data.paymentDue).endOf("day").toDate(),
            }),
        }),
        fulfillmentChangeReason: data.fulfillmentChangeReason ?? undefined,
        fulfillmentChangeReasonMessage:
          data.fulfillmentChangeReasonMessage.trim() || undefined,
      },
      {
        onSuccess: () => {
          track("Order Confirmed", {
            initiator: business?.Supplier ? "supplier" : "retailer",
            orderId: order.id,
            confirmedAt,
          });
          onSuccess?.();
          onDismiss();
        },
      },
    );
  };

  const isRequestLoading = orderConfirmMutation.isPending;
  const hasRequestError = orderConfirmMutation.isError;
  let requestErrorMessge;
  if (hasRequestError) {
    requestErrorMessge = apiV2AssertError(orderConfirmMutation.error)?.response
      ?.data.message;
  }

  return (
    <>
      <DialogNavigation
        leftIcon={isRequestLoading ? undefined : "CHEVRON_DOWN"}
        onClickLeftIcon={isRequestLoading ? undefined : () => onDismiss()}
        title={
          page === ConfirmOrderDialogPage.FULFILLMENT
            ? "Choose your courier service provider"
            : "Finalise this order"
        }
        description={
          page === ConfirmOrderDialogPage.FULFILLMENT
            ? "Which service provider do you want to use to ship this order?"
            : "This cannot be undone. Make sure the following order details are correct before proceeding as they cannot be changed once the order is confirmed."
        }
      />
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className={"flex flex-col gap-2"}
      >
        {isRequestLoading ? (
          <SpinnerLegacy />
        ) : (
          <>
            <DialogContent>
              {hasRequestError && (
                <Alert
                  type={"error"}
                  message={requestErrorMessge ?? "An unknown error occurred"}
                />
              )}
              {order.hasNoDraughtDutyRelief && (
                <FullDutyAlert
                  buyerName={order.Retailer.Business.name}
                  className={"my-2"}
                />
              )}
              <DialogPage page={ConfirmOrderDialogPage.FORM}>
                <div className="flex flex-col gap-2">
                  <ConfirmOrderModalFormFragment
                    modifyModal={false}
                    order={order}
                    form={form}
                  />
                </div>
              </DialogPage>
              <DialogPage page={ConfirmOrderDialogPage.FULFILLMENT}>
                <ConfirmOrderSellarShippingFulfillmentPage
                  form={form}
                  order={order}
                  fulfillmentType={fulfillmentType}
                />
              </DialogPage>
            </DialogContent>
            <DialogCTA className="flex flex-col gap-2">
              <BadPayerWarning
                flaggedAsBadPayer={order.Retailer.flaggedAsBadPayer}
                paymentType={order.paymentType}
              />
              {isSellarShipping(order, fulfillmentType) &&
              page === ConfirmOrderDialogPage.FORM ? (
                <Button type={"submit"}>Next</Button>
              ) : (
                <div className="flex items-center gap-2">
                  {page === ConfirmOrderDialogPage.FULFILLMENT && (
                    <Button
                      color={"transparent"}
                      onClick={() => setPage(ConfirmOrderDialogPage.FORM)}
                    >
                      Back
                    </Button>
                  )}
                  <Button type={"submit"} disabled={!isFulfillmentValid}>
                    Finalise
                  </Button>
                </div>
              )}
            </DialogCTA>
          </>
        )}
      </form>
    </>
  );
}
